'use client'

import { bookmarkTablature, removeTablature, setTablatureStatus, unbookmarkTablature } from "@/actions/tablatures";
import { t, trackEvent, useServerTranslations } from "@/lib";
import { event } from "@/lib/analytics/gtag";
import { hasPermission, UserRole } from "@/lib/auth/permissions";
import { Tablature } from "@/types/tablatures";
import { UserProfile } from "@/types/users";
import { Button } from "@ui/primitives";
import { useRouter } from "next/navigation";
import { useEffect, useState, useTransition } from "react";

export const TabEditButton = ({ tablature, profile, dictionary }: { tablature: Tablature, profile?: UserProfile, dictionary: Record<string, string> }) => {
    const router = useRouter();
    const [items, setItems] = useState<any[]>([]);
    const [isOwner, setOwner] = useState<boolean>(false);
    const [isPending, startTransition] = useTransition();

    const edit = () => {
        router.push(`/tablatures/studio?id=${tablature.id}`);
    }

    useEffect(() => {
        if (profile && ((tablature.user_id === +profile.id) || hasPermission(profile?.role, UserRole.MODERATOR))) {
            const it = []
            switch (tablature.status) {
                case 'public':
                    it.push({ label: t(dictionary, 'tablatures.status.private'), icon: 'fa-solid fa-check', command: () => startTransition(() => setTablatureStatus(tablature.id, "private")) });
                    break;
                case 'private':
                    it.push({ label: t(dictionary, 'tablatures.status.public'), icon: 'fa-solid fa-check', command: () => startTransition(() => setTablatureStatus(tablature.id, "public")) });
                    break;
            }

            if (tablature.status != 'deleted') {
                it.push({ label: t(dictionary, 'actions.delete'), icon: 'fa-solid fa-trash', command: () => startTransition(() => removeTablature(tablature.id)) });
            }
            if (tablature.status == 'deleted') {
                it.push({ label: t(dictionary, 'common.restore'), icon: 'fa-solid fa-check', command: () => startTransition(() => setTablatureStatus(tablature.id, "private")) });
            }
            setOwner(true)
            setItems(it)
        }
    }, [tablature, profile])

    return isOwner ?
        <Button onClick={edit} disabled={isPending} variant="ghost" size="sm" className="text-gray-500 hover:text-amber-500 hover:bg-amber-50" data-testid="tablature-edit-button">
            <i className="fa-solid fa-pen-to-square mr-2 group-hover:text-amber-500"></i>
            {t(dictionary, 'actions.edit')}
        </Button>
        : <></>
};

export const TabBookmarkButton = ({
    tablature,
    profile,
    isBookmarked: initialIsBookmarked }: { tablature: Tablature, profile?: UserProfile, isBookmarked: boolean }) => {
    const [isBookmarked, setIsBookmarked] = useState<boolean>(initialIsBookmarked);
    const [isPending, startTransition] = useTransition();
    const router = useRouter();

    const toggleBookmark = () => {
        if (!profile) {
            router.push('/login?redirect=' + encodeURIComponent(window.location.pathname));
            return;
        }

        startTransition(async () => {
            event({ action: isBookmarked ? 'unbookmark_tab' : 'bookmark_tab', category: 'tabs', value: tablature.id });
            if (isBookmarked) {
                await unbookmarkTablature(tablature.id);
                setIsBookmarked(false);
            } else {
                await bookmarkTablature(tablature.id);
                setIsBookmarked(true);
            }
            if (tablature.id) {
                trackEvent('tablature_bookmark', {
                    id: String(tablature.id),
                    action: isBookmarked ? 'unbookmark' : 'bookmark',
                });
            }
        });
    };

    return (
        <Button
            onClick={toggleBookmark}
            disabled={isPending}
            variant='ghost'
            data-testid="tablature-bookmark-button"
            className={`transition-colors duration-200 ${isBookmarked ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50' : 'text-gray-500 hover:text-amber-500 hover:bg-amber-50'}`}
        >
            <i className={`mr-2 ${isBookmarked ? "fa-solid fa-bookmark text-amber-500" : "fa-regular fa-bookmark text-gray-400 group-hover:text-amber-500"}`} />
            <span className="hidden sm:inline font-medium">
                {isBookmarked ? 'Sauvegardé' : 'Sauvegarder'}
            </span>
        </Button>
    );
};
